/**
 * Welcome Step
 *
 * Contextual welcome message based on how user arrived.
 * Issue #563 - First-Run Onboarding Flow
 */

import { ArrowRight } from 'lucide-react';
import type { Me, OnboardingContext } from '@/lib/api/auth';
import { BRAND_FULL } from '@/constants/brand';
import PhotoAttribution from '@/components/PhotoAttribution';
import ImageWithSkeleton from '@/components/ImageWithSkeleton';

interface WelcomeStepProps {
  user: Me;
  context: OnboardingContext;
  onNext: () => void;
}

export default function WelcomeStep({ user, context, onNext }: WelcomeStepProps) {
  // Contextual title
  const getTitle = () => {
    switch (context.type) {
      case 'club':
        return `Welcome to ${context.club_name}`;
      case 'event':
        return `You're set for ${context.event_name}`;
      case 'founder':
        return 'Welcome, Founder';
      case 'clean':
      default:
        return `Welcome to ${BRAND_FULL}`;
    }
  };

  const title = getTitle();
  const firstName = user.first_name || 'there';

  return (
    <div className="flex flex-col flex-1 min-h-0">
      {/* Progress Bar - Step 1 of 6 */}
      <div className="shrink-0 px-6 pt-4">
        <div className="w-full h-1 bg-oro-surface-alt rounded-full overflow-hidden">
          <div
            className="h-full bg-oro-gold rounded-full transition-all duration-300"
            style={{ width: '16%' }}
          />
        </div>
        <p className="text-text-muted text-xs mt-2 text-center">Step 1 of 6</p>
      </div>

      {/* Hero Image - Full Height */}
      <div className="flex-1 relative m-4 rounded-xl overflow-hidden">
        <ImageWithSkeleton
          src="/images/onboarding/hero-vertical-bridge-runner-fabiocomparelli.jpg"
          alt="Runner on a bridge at sunrise"
          loading="eager"
          className="w-full h-full object-cover"
        />
        <PhotoAttribution photographer="Fabio Comparelli" unsplashUsername="fabioha" />

        {/* Title overlay on image */}
        <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-6">
          <h2 id="onboarding-title" className="text-2xl font-semibold text-white mb-1">
            {title}
          </h2>
          <p className="text-white/80 text-sm">Hi {firstName}!</p>
        </div>
      </div>

      {/* Bottom-Docked Actions */}
      <div className="shrink-0 p-4 border-t border-oro-border bg-oro-surface">
        <button
          type="button"
          onClick={onNext}
          className="w-full px-4 py-3 rounded-lg bg-oro-gold hover:bg-oro-gold/90 shadow-lg flex items-center justify-center gap-2 transition-all"
        >
          <span className="text-black font-medium">Get Started</span>
          <ArrowRight className="w-5 h-5 text-black" />
        </button>
      </div>
    </div>
  );
}
